import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Icon, Menus, Text, useTheme } from '@Sage/hoi-poi-ui';

import { STATUS, SAVE_MODES, useTopolEditor } from './hooks/useTopolEditor';
import useTopolVariables from './hooks/useTopolVariables';
import Loading from 'components/Loading';
import TitleInput from '../MarketingCampaignsForm/TitleInput';
import { getLiteral } from 'utils/getLiteral';
import './styles.scss';

let instanceCounter = 0;

const EmailEditor = memo(
    ({
        isOpen = false,
        onSave,
        onExit,
        onPreview,
        emailConfig = '',
        title = '',
        editableTitle = false,
        onTitleChange,
    }) => {
        const theme = useTheme();
        const uniqueUserId = useSelector((state) => {
            const { idUsuario, idImplementacion } = state.config?.userData;
            return `${idUsuario}_${idImplementacion}`;
        });
        const [currentTitle, setCurrentTitle] = useState(title);
        const containerRef = useRef(null);
        // Generate unique ID for each instance to avoid DOM conflicts
        const containerId = useMemo(() => {
            instanceCounter += 1;
            return `topol-app-${instanceCounter}-${Date.now()}`;
        }, []);

        const {
            variables,
            loading: variablesLoading,
            error,
            retry,
        } = useTopolVariables({ isOpen });

        // Sync currentTitle when title prop changes
        useMemo(() => {
            setCurrentTitle(title);
        }, [title]);

        const handleTitleChange = useCallback(
            (newTitle) => {
                setCurrentTitle(newTitle);
                onTitleChange?.(newTitle);
            },
            [onTitleChange],
        );

        const {
            status,
            retry: retryEditor,
            save,
        } = useTopolEditor({
            containerRef,
            containerId,
            config: emailConfig,
            theme,
            onSave,
            onPreview,
            isOpen,
            variables,
            userId: uniqueUserId,
        });

        const handleExitWithoutSaving = useCallback(() => {
            onExit?.();
        }, [onExit]);

        const menuItems = [
            {
                title: getLiteral('action_exit_without_saving'),
                icon: <Icon name="logout" />,
                onClick: handleExitWithoutSaving,
            },
        ];

        if (!isOpen) {
            return null;
        }

        return (
            <div className="email-campaigns">
                <div className="email-campaigns__topbar">
                    <div className="email-campaigns__topbar-left">
                        {editableTitle ? (
                            <TitleInput
                                title={currentTitle}
                                onChange={handleTitleChange}
                                onSave={handleTitleChange}
                            />
                        ) : (
                            !!currentTitle?.trim() && (
                                <Text
                                    type="h6"
                                    className="email-campaigns__campaign-title"
                                    isTruncated
                                    useTooltip
                                >
                                    {currentTitle}
                                </Text>
                            )
                        )}
                    </div>
                    <div className="email-campaigns__topbar-right">
                        <Button
                            type="secondary"
                            onClick={() => save(SAVE_MODES.PREVIEW)}
                            isDisabled={status !== STATUS.READY || variablesLoading}
                        >
                            {getLiteral('label_marketing_campaigns_preview_and_test')}
                        </Button>
                        <Button
                            onClick={() => save(SAVE_MODES.SAVE)}
                            isDisabled={status !== STATUS.READY || variablesLoading}
                        >
                            {getLiteral('action_save_and_exit')}
                        </Button>
                        <Menus
                            size="small"
                            placement="bottom-end"
                            items={menuItems}
                            classNameMenu="email-campaigns__menu-popover"
                            icon={<Icon name="threeDots" />}
                        />
                    </div>
                </div>
                {error && (
                    <div className="email-campaigns__error">
                        <Text type="body" color="danger">
                            {getLiteral('error_unexpected')}
                        </Text>
                        <Button onClick={retry} color="primary">
                            {getLiteral('label_retry')}
                        </Button>
                    </div>
                )}

                {status === STATUS.INITIALIZING && !variablesLoading && (
                    <div className="email-campaigns__loading">
                        <Loading />
                    </div>
                )}

                {status === STATUS.ERROR && !error && (
                    <div className="email-campaigns__error">
                        <Text type="body" color="danger">
                            {getLiteral('error_unexpected')}
                        </Text>
                        <Button onClick={retryEditor} color="primary">
                            {getLiteral('label_retry')}
                        </Button>
                    </div>
                )}

                <div
                    id={containerId}
                    ref={containerRef}
                    className={classnames('email-campaigns__editor', {
                        'email-campaigns__editor--ready': status === STATUS.READY,
                    })}
                />
            </div>
        );
    },
);

EmailEditor.displayName = 'EmailEditor';

EmailEditor.propTypes = {
    isOpen: PropTypes.bool,
    onSave: PropTypes.func,
    onExit: PropTypes.func,
    onPreview: PropTypes.func,
    emailConfig: PropTypes.string,
    title: PropTypes.string,
    editableTitle: PropTypes.bool,
    onTitleChange: PropTypes.func,
};

export default EmailEditor;
